import ajax from '../../utilities/ajax';
import bugsnagClient from '../../global/bugsnag';
import CartService from 'JS/services/CartService';
import CustomerListService from '../../services/CustomerListService';
import productHelper from '../helpers/ProductHelper';
import ProductService, { AddBundleToCartRequest, AddProductComparisonToCartRequest } from '../../services/ProductService';
import Vue from 'vue';
import Vuex, { ActionTree, MutationTree } from 'vuex';
import { calculateMaxBuyableQty, calculateMinBuyableQty, validateQuantityOverProductMin, validateQuantityUnderProductMax, validateQuantityWithinInterval } from 'JS/utilities/product/quantity';
import { CartUpdateResolutionType, ICartZipcodeUpdateResponse } from 'JS/types/Order';
import { FulfillmentType } from '../../types/ProductDelivery';
import { IProductDetailPageJson, IProductDetailProductJson, IProductShipRateResponse, IUpsellBundle } from 'JS/types/PDP';
import { jsonCopy } from '../../helpers/functions';
import { PDPGetters, PDPRootState } from './types';
import { ProductSubscriptionStatus } from '../../types/Order';

Vue.use(Vuex);

const customerListService = new CustomerListService();
const productService = new ProductService();
const cartService = new CartService();

/*============================================================
 == State
/============================================================*/

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createStore(storeData: IProductDetailPageJson) {

	const state = (): PDPRootState => ({
		addingToCart: false,
		activeImages: [],
		allAttributes: [],
		childSelectors: null,
		singleValueSelectors: null,
		currentZip: null,
		details: null,
		fetchingProduct: false,
		googleApiKey: null,
		isTouchDevice: false,
		localStore: null,
		loggedIn: false,
		customerEmail: null,
		outOfMarket: false,
		product: null,
		quantity: 1,
		selectedAttributes: {},
		isGroupUrl: false,
		shippingDeliveryDate: null,
		shippingDeliveryDateNew: null,
		shippingLoading: true,
		shippingMessage: null,
		shippingMessageMobile: null,
		isShippingPromoRewardsOnly: false,
		shippingError: null,
		showStoreDetailsModal: false,
		variants: [],
		customerHasRegistries: false,
		isPLA: false,
		buyAgainData: null,
		fulfillmentType: null,
		defaultFulfillmentType: null,
		visitorIsInMarket: true,
		subscriptionFrequency: null,
		carousels: {
			plaCarousel: null,
			beforeProductInfo: [],
			afterProductInfo: [],
			bottomOfPage: [],
			sponsoredProducts: null
		},
		upsellBundle: null,
		productPopularity: null,
		showCompareSimilar: false,
		disableAddToCartButton: false,
		currentRewardsShippingOffer: null,
		flagText: null,
	});

	/*============================================================
	 == Mutations
	/============================================================*/

	const mutations: MutationTree<PDPRootState> = {
		addingToCart(state, val) {
			state.addingToCart = val;
		},
		product(state, val) {
			state.product = val;
		},

		upsellBundle(state, val) {
			state.upsellBundle = val;
		},

		productPopularity(state, val) {
			state.productPopularity = val;
		},

		currentRewardsShippingOffer(state, val) {
			state.currentRewardsShippingOffer = val;
		},

		flagText(state, val) {
			state.flagText = val;
		},

		fetchingProduct(state, val) {
			state.fetchingProduct = val;
		},

		selectedAttributes(state, val) {
			state.selectedAttributes = val;
		},

		activeImages(state, val) {
			state.activeImages =  val;
		},

		quantity(state, val) {
			state.quantity = val;
		},

		showStoreDetailsModal(state, val) {
			state.showStoreDetailsModal = val;
		},

		currentZip(state, val) {
			state.currentZip = val;
		},

		isTouchDevice(state, val) {
			state.isTouchDevice = val;
		},

		shippingLoading(state, val) {
			state.shippingLoading = val;
		},

		shippingError(state, val) {
			state.shippingError = val;
		},

		shippingMessage(state, val) {
			state.shippingMessage = val;
		},

		shippingMessageMobile(state, val) {
			state.shippingMessageMobile = val;
		},

		isShippingPromoRewardsOnly(state, val) {
			state.isShippingPromoRewardsOnly = val;
		},

		shippingDeliveryDate(state, val) {
			state.shippingDeliveryDate = val;
		},

		shippingDeliveryDateNew(state, val) {
			state.shippingDeliveryDateNew = val;
		},

		childSelectors(state, val) {
			state.childSelectors = val;
		},

		singleValueSelectors(state, val) {
			state.singleValueSelectors = val;
		},

		details(state, val) {
			state.details = val;
		},

		allAttributes(state, val) {
			state.allAttributes = val;
		},

		localStore(state, val) {
			state.localStore = val;
		},

		variants(state, val) {
			state.variants = val;
		},

		loggedIn(state, val) {
			state.loggedIn = val;
		},

		outOfMarket(state, val) {
			state.outOfMarket = val;
		},

		fulfillmentType(state, val) {
			state.fulfillmentType = val;
		},

		backInStockInfo(state, val) {
			state.product.backInStockInfo = val;
		},
		subscriptionFrequency(state, val) {
			state.subscriptionFrequency = val;
		},
		isGroupUrl(state, val) {
			state.isGroupUrl = val;
		}
	};

	/*============================================================
	 == Actions
	/============================================================*/

	const actions: ActionTree<PDPRootState, PDPRootState> = {
		initDefaultFulfillment({ state, dispatch }) {
			const { buyAgainData, defaultFulfillmentType } = state;

			if (buyAgainData && buyAgainData.inStockForFulfillmentType) {
				dispatch('setDeliveryType',
					{ fulfillmentType: buyAgainData.fulfillmentType, allowDisabledOptions: true });
			} else if (defaultFulfillmentType) {
				dispatch('setDeliveryType', { fulfillmentType: defaultFulfillmentType, allowDisabledOptions: true });
			}
		},

		autoSelectFulfillmentTypeIfNecessary({ state, getters, dispatch }, forceDefault = false) {
			const { fulfillmentType, product } = state;
			const { defaultFulfillmentType } = product;
			const { isShippingDisabled, isPickupDisabled, isDeliveryDisabled } = getters;

			const selectable = {
				[FulfillmentType.StandardShipping]: !isShippingDisabled,
				[FulfillmentType.InStorePickup]: !isPickupDisabled,
				[FulfillmentType.LocalDelivery]: !isDeliveryDisabled,
			};

			const canSelectDefaultFulfillmentType = !!selectable[defaultFulfillmentType];
			const canSelectCurrentFulfillmentType = !!selectable[fulfillmentType];

			if ((!canSelectCurrentFulfillmentType || forceDefault) && canSelectDefaultFulfillmentType) {
				dispatch('setDeliveryType',
					{ fulfillmentType: defaultFulfillmentType, allowDisabledOptions: true });
			}

		},

		addToCart({ state }, payload: { userConfirmed: boolean, userAgreed: boolean} ) {
			const {
				userConfirmed,
				userAgreed
			} = payload;
			return productService.addToCart({
				blainNumber: state.product.blainNumber,
				qty: state.quantity,
				fulfillmentType: state.fulfillmentType,
				userConfirmed,
				userAgreed,
				updateSubscriptionRequest: state.subscriptionFrequency != null ? { frequency: state.subscriptionFrequency } : null,
				skipUpsell: false,
				ignoreFulfillmentType: false
			});
		},

		addBundleToCart(_, request: AddBundleToCartRequest) {
			return productService.addBundleToCart(request);
		},

		addProductComparisonToCart(_, request: AddProductComparisonToCartRequest) {
			return productService.addProductComparisonToCart(request);
		},

		getProduct({ commit, state, dispatch }, blainNumber: string) {
			commit('fetchingProduct', true);
			return ajax.get<IProductDetailProductJson>(`/products/a/getproduct/?blainNumber=${blainNumber}`)
				.then((product) => {
					commit('product', product);
					const queryString = window.location.search;
					window.history.replaceState({ "pageTitle": product.name }, product.name, product.virtualUrl + queryString);

					if (state.product.activeOnECommerce) {
						dispatch('getShippingInfo');
					}
					commit('fetchingProduct', false);
				})
				.catch((err) => {
					commit('fetchingProduct', false);
					bugsnagClient.notify(err);
				});
		},

		async getUpsellBundleForProduct({ state, commit, getters }) {
			if (getters.hasUpsellBundle) return;

			let bundle: IUpsellBundle;

			try {
				bundle = await productService.getUpsellBundleForProduct(state.product.blainNumber);
			} catch (error) {
				bugsnagClient.notify(error);
			}

			if (!bundle) return;

			commit('upsellBundle', bundle);
		},

		selectAttribute({ state, commit, dispatch }, attribute) {
			const name = attribute.selector;
			const value = attribute.value;
			// If the attribute is already selected, do nothing.
			if (state.selectedAttributes[name] === value) return;

			const selectors = jsonCopy(state.childSelectors);
			const selectedAttributes = jsonCopy(state.selectedAttributes);
			selectedAttributes[name] = value;

			// Get remaining variants
			const numSelected = Object.keys(selectedAttributes).length;
			const allSelected = numSelected === selectors.length;
			const otherSelectors = selectors.filter(x => x.selector !== name);
			let productsToCheck = [];
			const remainingVariants = productHelper.getRemainingVariants(selectedAttributes, state.variants);
			const hadPreviouslyFulfilledAllSelectors = Object.keys(state.selectedAttributes).length === selectors.length;


			// Update selected attributes
			productHelper.updateSelectedAttribute(selectors, attribute);
			productsToCheck = numSelected === 1
				? remainingVariants
				: productHelper.getProductsByAttribute(attribute, state.variants);

			// Clear any selector errors
			selectors.forEach(x => x.showSelectionError = false);

			// This action should really be an async function so we can conditionally await
			// the dispatch to getProduct and flatten updateSelectorsAndAttributes to always run at the end,
			// but IE blows up, so this is how it has to be done for now
			// (unless we configure babel correctly)

			const childProductSelected = allSelected && remainingVariants.length === 1;

			function updateSelectorsAndAttributes(): void {
				productHelper.updateAttributeAvailability(otherSelectors, productsToCheck);
				productHelper.updateSelectionErrors(otherSelectors);
				commit('selectedAttributes', selectedAttributes);
				commit('childSelectors', selectors);
				dispatch('setActiveImagesFromAttribute', attribute);
				dispatch('autoSelectFulfillmentTypeIfNecessary', !hadPreviouslyFulfilledAllSelectors);
				dispatch('getUpsellBundleForProduct');
			}

			// If there is only one remaining variant, get the product data and set active
			if (childProductSelected) {
				commit('isGroupUrl', !childProductSelected);
				return dispatch('getProduct', remainingVariants[0].blainNumber)
					.then(updateSelectorsAndAttributes);
			} else {
				updateSelectorsAndAttributes();
			}
		},

		setActiveImagesFromAttribute({ state, commit, getters }, { swatchUrl, selector, value }) {
			let productWithImages;
			if (swatchUrl) {
				productWithImages = state.variants.find(p => p.attributes[selector] === value);
			} else if (getters.remainingSelectors.length === 0 && getters.remainingVariants.length > 0) {
				productWithImages = getters.remainingVariants[0];
			}
			if (productWithImages) commit('activeImages', productWithImages.images);
		},

		setDeliveryType({ state, getters, commit, dispatch }, deliveryRequest: { fulfillmentType: FulfillmentType, allowDisabledOptions: boolean }) {
			if (deliveryRequest.allowDisabledOptions && state.fulfillmentType === deliveryRequest.fulfillmentType) {
				return; //Test option in A/B test of pdp ui
			}

			if (!deliveryRequest.allowDisabledOptions && (
				(deliveryRequest.fulfillmentType === FulfillmentType.StandardShipping && getters.isShippingDisabled) ||
				(deliveryRequest.fulfillmentType === FulfillmentType.InStorePickup && getters.isPickupDisabled) ||
				(deliveryRequest.fulfillmentType === FulfillmentType.LocalDelivery && getters.isDeliveryDisabled)
			)) {
				return;
			}

			commit('fulfillmentType', deliveryRequest.fulfillmentType);
		},

		changeStore(_, storeNum) {
			return ajax.get(`/stores/set-json/${storeNum}`);
		},

		async updateShipToZip({ commit, dispatch }, shipToZip: string): Promise<ICartZipcodeUpdateResponse> {
			const response = await cartService.updateShipToZip(shipToZip);
			if (response.resolutionType == CartUpdateResolutionType.Success
				|| response.resolutionType == CartUpdateResolutionType.CartNotFound) {
				commit('currentZip', shipToZip);
				dispatch('getShippingInfo');
			}
			return response;
		},

		getShippingInfo({ commit, state, dispatch }) {
			commit('shippingLoading', true);
			const zip = state.currentZip;
			ajax.post<IProductShipRateResponse,
			 { blainNumber: string,
				isGroupUrl: boolean,
				zipcode: string
			}>(`/products/a/getshippinginfo`, {
				blainNumber: state.product.blainNumber,
				isGroupUrl: state.isGroupUrl,
				zipcode: zip
			}).then(res => {
				const { zipcode, success } = res;
				commit('shippingLoading', false);
				commit('currentZip', zipcode);
				if (res && success == false) {
					commit('shippingDeliveryDate', null);
					commit('shippingDeliveryDateNew', null);
					commit('shippingError', `Shipping estimate is not available for ${zipcode}`);
					return;
				}
				commit('shippingMessage', res.shippingMessage);
				commit('shippingMessageMobile', res.shippingMessageMobile);
				commit('isShippingPromoRewardsOnly', res.isShippingPromoRewardsOnly);
				commit('shippingDeliveryDateNew', res.shippingDeliveryDateNew);
				commit('shippingDeliveryDate', res.shippingDeliveryDate);
			}).catch(err => {
				dispatch('resetShippingEstimate');
				bugsnagClient.notify(err);
				commit('shippingLoading', false);
			});
		},

		resetShippingEstimate({ commit }) {
			commit('shippingMessage', null);
			commit('shippingMessageMobile', null);
			commit('isShippingPromoRewardsOnly', false);
			commit('shippingDeliveryDate', null);
			commit('shippingDeliveryDateNew', null);
		},

		setSelectorError({ state, commit }, attribute) {
			const newChildSelectors = jsonCopy(state.childSelectors);
			for (let i = 0; i < newChildSelectors.length; i++) {
				if (attribute === newChildSelectors[i].selectorLabel) {
					newChildSelectors[i].showSelectionError = true;
					break;
				}
			}
			commit('childSelectors', newChildSelectors);
		},

		selectProductAttributes({ state, dispatch }, blainNumber) {
			const product = state.variants.find(x => x.blainNumber === blainNumber);
			const atts = product.attributes;
			if (!atts) return;


			// Ideally, this would be an async method and the calls to selectAttribute
			// would simply be awaited, but IE blows up and this is how it has to be for now
			// (unless we configure babel correctly)

			let promiseChain = Promise.resolve();

			for (const selector in atts) {
				promiseChain = promiseChain.then(() =>
					dispatch("selectAttribute", {
						selector,
						value: atts[selector]
					})
				);
			}

			return promiseChain;
		},

		getRegistryList(_) {
			return customerListService.getCustomerLists();
		},

		addProductToRegistry({ state }, listID) {
			const { quantity, product: { blainNumber } } = state;

			return customerListService.addItemToList(blainNumber, listID, quantity);
		},

		updateAlreadySubscribed({ state, commit }, { fulfillmentType, subscribed }) {
			const { backInStockInfo } = state.product;
			const { forFulfillmentType } = backInStockInfo;
			const fulfillmentTypeInfo = forFulfillmentType[fulfillmentType];

			if (!fulfillmentTypeInfo) return;

			commit('backInStockInfo', {
				...backInStockInfo,
				forFulfillmentType: {
					...forFulfillmentType,
					[fulfillmentType]: {
						...fulfillmentTypeInfo,
						isAlreadySubscribed: subscribed,
					}
				}
			});
		},

		receiveUnitPriceFromCart({ state, commit }, realPrice) {
			const { product } = state;
			const { price } = product;

			commit('product', {
				...product,
				price: {
					...price,
					price: realPrice.startsWith('$') ? realPrice : '$' + realPrice,
				}
			});
		}
	};

	/*============================================================
	 == Getters
	/============================================================*/

	const getters: { [K in keyof PDPGetters]: (state: PDPRootState, getters: PDPGetters) => PDPGetters[K] } = {
		remainingSelectors(state, getters) {
			return state.childSelectors.filter(sel => {
				const selectorNames = getters.selectedSelectors;
				return !selectorNames.some(x => x === sel.selector);
			});
		},

		hasSelectorError(state) {
			return state.childSelectors.some(selector => selector.showSelectionError);
		},

		hasSelectedVariant(state, getters) {
			return getters.remainingSelectors.length == 0 && !getters.hasSelectorError;
		},

		remainingVariants(state, getters) {
			const selected = state.selectedAttributes;
			const selectedNames = getters.selectedSelectors;
			if (selectedNames.length === 0) return state.variants;
			return state.variants.filter(product => {
				let isRemaining = true;
				for (let i = 0; i < selectedNames.length; i++) {
					if (product.attributes[selectedNames[i]] !== selected[selectedNames[i]]) {
						isRemaining = false;
					}
				}
				return isRemaining;
			});
		},

		selectedSelectors(state) {
			return Object.keys(state.selectedAttributes);
		},

		hasImages(state) {
			return state.variants.some(x => x.images.length > 1);
		},

		rewardsPoints(state, getters) {
			const { rewardsPoints } = state.product.price;

			if (rewardsPoints == null) {
				return null;
			}

			let points = Number(rewardsPoints) * state.quantity;
			if (getters.quantityExceedsStock) {
				points = points / state.quantity;
			}

			const { subscriptionInfo } = state.product;
			if (subscriptionInfo.status === ProductSubscriptionStatus.Active && state.fulfillmentType === FulfillmentType.InStorePickup) {
				points = points * subscriptionInfo.rewardsPointsMultiplier;
			}

			// @ts-ignore
			return parseFloat(Math.round(points * 100) / 100);
		},

		minBuyableQty(state) {
			return calculateMinBuyableQty(state.product);
		},

		maxBuyableQty(state) {
			return calculateMaxBuyableQty(state.fulfillmentType, state.localStore, state.product);
		},

		isSelectedForShipping(state) {
			return state.fulfillmentType === FulfillmentType.StandardShipping;
		},

		isSelectedForDelivery(state) {
			return state.fulfillmentType === FulfillmentType.LocalDelivery;
		},

		isSelectedForStorePickup(state) {
			return state.fulfillmentType === FulfillmentType.InStorePickup;
		},

		quantityLessThanMin(state) {
			return validateQuantityOverProductMin(state.quantity, state.product);
		},

		quantityOutsideInterval(state) {
			return validateQuantityWithinInterval(state.quantity, state.product);
		},

		quantityGreaterThanMax(state) {
			return validateQuantityUnderProductMax(state.quantity, state.product);
		},

		showSpecialMessageBox(_, getters) {
			return getters.isInStore || getters.isQuote || getters.isComing;
		},

		showMastercardCallout(state) {
			return state.product.showMastercardCallout;
		},

		isInStore(state, getters) {
			return state.product.showInStoreOnlyMessage || state.product.showOutMarketPickupForm || (state.outOfMarket && getters.inStoreOnly);
		},

		inStoreOnly(state) {
			return state.product.activeInStorePickup && !state.product.activeOnECommerce;
		},

		isQuote(state) {
			return state.product.showGetQuote;
		},

		isComing(state) {
			return state.product.isComingSoon;
		},

		isTire(state) {
			return state.product.isTire;
		},

		quantityExceedsStock(state, getters) {
			return state.quantity > getters.maxBuyableQty;
		},

		isAvailableOnline(state) {
			return state.product.activeOnECommerce;
		},

		isInStockForShip(state) {
			return state.product.shipEcomOnHand > 0 && !state.product.isSoldOut;
		},

		isInStockForBopus(state) {
			return state.product.storeEcomOnHand > 0 && !state.product.isSoldOut;
		},

		isInStockInStore(state) {
			return state.product.storeNetOnHand > 0 && !state.product.isSoldOut;
		},

		selectedFulfillmentType(state) {
			return state.fulfillmentType;
		},

		productHasFulfillmentError(state) {
			return (state.fulfillmentType === FulfillmentType.StandardShipping && !state.product.activeOnECommerce)
				|| (state.fulfillmentType === FulfillmentType.InStorePickup && !state.product.activeInStorePickup)
				|| (state.fulfillmentType === FulfillmentType.LocalDelivery && !state.product.activeLocalDelivery);
		},

		productHasStock(state) {
			const { product, fulfillmentType } = state;
			if (
				(product.activeInStorePickup && product.storeEcomOnHand <= 0 && fulfillmentType !== FulfillmentType.StandardShipping)
				||
				(product.activeOnECommerce && product.shipEcomOnHand <= 0 && fulfillmentType === FulfillmentType.StandardShipping)
			) {
				return false;
			} else {
				return true;
			}
		},

		hasAdditionalCharge(state) {
			return state.product.additionalCharge && state.product.additionalCharge.hasAdditionalCharge;
		},

		isReadyToSelectFulfillmentOption(state, getters) {
			return getters.remainingSelectors.length === 0 && !state.fetchingProduct;
		},

		isShippingDisabled(state, getters) {
			return !getters.isInStockForShip || getters.inStoreOnly || !state.product.activeOnECommerce;
		},

		isPickupDisabled(state, getters) {
			return !getters.isInStockForBopus
					|| !state.product.activeInStorePickup
					|| state.outOfMarket
					|| state.localStore.comingSoon;
		},

		isDeliveryDisabled(state, getters) {
			return !getters.isInStockForBopus
					|| !state.product.activeLocalDelivery
					|| state.outOfMarket
					|| state.localStore.comingSoon;
		},

		showTopLevelBackInStockForm(state, getters) {
			const storeEcomInStock = state.product.storeEcomOnHand > 0 && !state.product.isSoldOut;
			const storeNetInStock = state.product.storeNetOnHand > 0 && !state.product.isSoldOut;
			const shipEcomInStock = state.product.shipEcomOnHand > 0;
			const notCarriedInStore = state.product.notCarriedInStore;
			const isEligibleForStockNotifications = state.product.backInStockInfo.isProductEligible;

			return isEligibleForStockNotifications 
				&& getters.remainingSelectors.length === 0 
				&& !storeEcomInStock 
				&& !shipEcomInStock 
				&& !storeNetInStock 
				&& !notCarriedInStore //show not carried messaging for clarity
				&& !getters.inStoreOnly;
		},

		hasUpsellBundle(state) {
			return state.upsellBundle && state.upsellBundle.products.map(p => p.blainNumber).includes(state.product.blainNumber);
		},
		addingToCart(state) {
			return state.addingToCart;
		}

	};

	const store = new Vuex.Store({
		state,
		mutations,
		actions,
		getters,
	});

	if (storeData) {
		store.state.details = storeData;
		store.state.product = storeData.product;
		store.state.activeImages = storeData.product.images;
		store.state.allAttributes = storeData.attributes;
		store.state.childSelectors = storeData.childSelectors;
		store.state.singleValueSelectors = storeData.singleValueSelectors;
		store.state.localStore = storeData.localStore;
		store.state.variants = storeData.variantProducts;
		store.state.isGroupUrl = storeData.isGroupUrl;
		store.state.loggedIn = storeData.loggedIn;
		store.state.customerEmail = storeData.customerEmail;
		store.state.outOfMarket = storeData.outOfMarket;
		store.state.currentZip = storeData.zipCode;
		store.state.googleApiKey = storeData.googleApiKey;
		store.state.customerHasRegistries = storeData.customerHasRegistries;
		store.state.isPLA = storeData.isPLA;
		store.state.buyAgainData = storeData.buyAgainData;
		store.state.fulfillmentType = storeData.product.defaultFulfillmentType;
		store.state.defaultFulfillmentType = storeData.defaultFulfillmentType;
		store.state.quantity = storeData.defaultQuantity || 1;
		store.state.visitorIsInMarket = storeData.visitorIsInMarket;
		store.state.carousels = storeData.productCarousels;
		store.state.upsellBundle = storeData.upsellBundle;
		store.state.productPopularity = storeData.productPopularity;
		store.state.currentRewardsShippingOffer = storeData.currentRewardsShippingOffer;
		store.state.flagText = storeData.flagText;
		store.state.showCompareSimilar = storeData.showCompareSimilar;
		store.state.disableAddToCartButton = storeData.disableAddToCartButton;
	}

	return store;
}

export default createStore((window as any).productjson);